import React, { useMemo } from 'react';
import DraggableTable from 'components/blazeTable/Draggable/DraggableTable';
import { HttpTransaction, Transaction, TransactionType } from 'types/rest/assetCatalogTypes';
import { BlazeTableColDef } from 'components/blazeTable/BlazeTable.types';
import i18n from 'i18n';
import { getColoredHttpMethod } from 'utils/commonUtil';
import _ from 'lodash';
import BzmIcon from 'components/icons/BzmIcon';
import TransactionSimpleDetail from 'components/transactionsShuttle/transactionsSimpleTable/TransactionSimpleDetail';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import { MockService } from 'types/rest/mockServiceTypes';
import clsx from 'clsx';
import SimpleTagList from 'components/ui/tags/SimpleTagList';
import VirtualServiceTypeIcon from '../../../../../components/icons/VirtualServiceTypeIcon';

export enum Move {
  start,
  end,
}

interface DraggableTransactionsPriorityTableProps {
  isLoading: boolean;
  transactions: Transaction[];
  context: MockService | MockServiceTemplate;
  onSelectionChange: (items: any[]) => void;
  onDragFinished: (items: any[]) => void;
  moveTransaction: (transaction: Transaction, where: Move) => void;
  selectedItems: number[] | null;
  type: TransactionType;
}
const DraggableTransactionsPriorityTable = (props: DraggableTransactionsPriorityTableProps) => {
  const getSize = () => useMemo(() => _.size(props.transactions), [props.transactions]);

  const getTableColumns = () => {
    // Definition of table columns
    const colsDefinition: BlazeTableColDef[] = [
      {
        header: i18n.t('transactions:txnTable.id'),
        accessor: 'id',
        width: 100,
      },
      {
        header: i18n.t('transactions:txnTable.name'),
        accessor: 'name',
        cellComponent: (transaction: Transaction) => {
          const icon = <VirtualServiceTypeIcon type={transaction.type} className={'mr-2 align-middle'} />;
          return (
            <span>
              <span className={'position-relative'} style={{ top: 3 }}>
                {icon}
              </span>{' '}
              {transaction.name}
            </span>
          );
        },
      },
      {
        accessor: 'method',
        cellComponent: (transaction: HttpTransaction) => {
          return getColoredHttpMethod(transaction.dsl.requestDsl.method as any);
        },
        header: 'Method',
        isSortable: false,
        width: 100,
      },
      {
        accessor: 'url',
        cellComponent: (transaction: HttpTransaction) => {
          return <span>{_.get(transaction, 'dsl.requestDsl.url.matchingValue')}</span>;
        },
        header: 'Request URL',
      },
      {
        accessor: 'dsl.responseDsl.status',
        cellComponent: (transaction: HttpTransaction) => {
          return <span>{_.get(transaction, 'dsl.responseDsl.status')}</span>;
        },
        header: 'Response Status',
        isSortable: false,
        width: 130,
      },
      {
        accessor: 'serviceName',
        cellComponent: (transaction: Transaction) => {
          return <span>{transaction.serviceName}</span>;
        },
        header: 'Service',
        isSortable: false,
        width: 200,
      },
      {
        accessor: 'tags',
        cellComponent: (transaction: Transaction) => {
          return (
            <div className={`qa-tbl-row-transaction-tags-${transaction.id} picked`}>
              {transaction.tags != null && <SimpleTagList tags={transaction.tags} />}
            </div>
          );
        },
        header: 'Tags',
        width: 180,
      },
      {
        accessor: 'priority',
        header: 'Priority',
        width: 180,
      },
      {
        accessor: 'actions',
        cellComponent: (transaction: Transaction) => {
          const isFirst = transaction.priority === 1;
          const isLast = transaction.priority === getSize();
          return (
            <div className="flex-table-actions text-center">
              <BzmIcon
                onClick={() => {
                  if (isFirst) {
                    return;
                  }
                  props.moveTransaction(transaction, Move.start);
                }}
                variant={'icon-bzm-move-up'}
                className={clsx(isFirst ? 'text-muted' : 'hand-cursor', 'mr-3')}
                title={'Move transaction to beginning.'}
              />

              <BzmIcon
                onClick={() => {
                  if (isLast) {
                    return;
                  }
                  props.moveTransaction(transaction, Move.end);
                }}
                variant={'icon-bzm-move-down'}
                className={clsx(isLast ? 'text-muted' : 'hand-cursor')}
                title={'Move transaction to end.'}
              />
            </div>
          );
        },
        isSortable: false,
        header: 'Actions',
        width: 100,
      },
    ];

    return colsDefinition;
  };

  const getExpandComponent = (item: Transaction) => (
    <TransactionSimpleDetail
      transaction={item}
      context={props.context}
      disabled={false}
      showTransactionLink={false}
      showTxnPriority={false}
      onUpdateTransaction={_.noop}
      transactionType={item.type}
    />
  );

  return (
    <DraggableTable
      cols={getTableColumns()}
      data={props.transactions}
      onSelectionChange={props.onSelectionChange}
      onDragFinished={props.onDragFinished}
      isLoading={props.isLoading}
      expandableComponent={getExpandComponent}
      showSelectAll={false}
      selectedItems={props.selectedItems}
      sortBy={{ sortKey: 'priority', readonly: true, direction: 'asc' }}
    />
  );
};

export default DraggableTransactionsPriorityTable;
